/**
 * Donation form module.
 *
 * The module automatically manages all donation forms on DOMContentLoaded event.
 *
 * @module donation_form
 */

/** @constant {String} - CSS class of the donation form element. */
const DONATION_FORM_CLASS = 'donation-form'
/** @constant {String} - CSS class of the parent element of donator detail fields. */
const DONATOR_DETAILS_CLASS = 'donator-details'
/** @constant {String} - The name attribute of the HTML checkbox to toggle donator details visibility. */
const CONFIRMATION_CHECKBOX_NAME = 'confirmation'
/** @constant {String} - CSS class to hide a HTML element. */
const HIDDEN_CLASS = 'hidden'
/** @constant {String} - CSS class for required form fields. */
const REQUIRED_CLASS = 'required'

/** Manages a single donation form. */
export class DonationForm {
    /**
     * Create new DonationForm.
     *
     * @param {HTMLFormElement} form - The HTML form to manage.
     * @throws {Error} When a confirmation checkbox or donator details cannot be found.
     */
    constructor(form) {
        this.form = form
        this.details = form.querySelector(`.${DONATOR_DETAILS_CLASS}`)
        this.checkbox = form.elements[CONFIRMATION_CHECKBOX_NAME]
        this.saved_values = {}

        if (!this.details || !this.checkbox) {
            throw new Error('Cannot find donation form subelements.')
        }

        this.checkbox.addEventListener('change', () => this.toggleDetails(this.checkbox.checked))
        this.toggleDetails(this.checkbox.checked)

        for (const list of this.details.querySelectorAll(`li`)) {
            if (list.querySelector('input')) {
                list.querySelector('input').placeholder = list.querySelector('label').textContent
                this.activeInput(list)
            }
        }
    }

    /**
     * Toggle visibility label
     *
     * @param {HTMLLIElement} list
     */
    activeInput(list) {
        const listInput  = list.querySelector('input')
        const toggleLabel = () => {
            listInput.value.length > 0
                ? list.querySelector('label').classList.add('show')
                : list.querySelector('label').classList.remove('show')
        }
        toggleLabel()
        listInput.addEventListener('input', toggleLabel)
        listInput.addEventListener('change', toggleLabel)
    }

    /**
     * Toggle visibility of donator details and make fields required as needed.
     *
     * @param {Boolean} visible - Whether donator details should be shown.
     */
    toggleDetails(visible) {
        if (visible) {
            this.details.classList.remove(HIDDEN_CLASS)
        } else {
            this.details.classList.add(HIDDEN_CLASS)
        }

        // Fill or erase values filled in the form
        if (visible) {
            for (const elm of this.details.querySelectorAll('input')) {
                if (elm.name in this.saved_values) {
                    elm.value = this.saved_values[elm.name]
                }
            }
        } else {
            // There is an animation of hiding the form. Let's remove the values after the form is hidden.
            setTimeout(() => {
                for (const elm of this.details.querySelectorAll('input')) {
                    this.saved_values[elm.name] = elm.value
                    elm.value = ''
                }
            }, 500)
        }

        // Swap the `required` inputs attribute
        for (const elm of this.details.querySelectorAll(`.${REQUIRED_CLASS} input`)) {
            elm.required = visible
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    for (const form of document.querySelectorAll(`.${DONATION_FORM_CLASS}`)) {
        new DonationForm(form)
    }
})
